// ============================================================================
// KLINISOL CRO - Authentication Context (WITH 2FA)
// ============================================================================

import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Check if user is logged in on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            verifyToken();
        } else {
            setLoading(false);
        }
    }, []);

    // Verify token with backend
    const verifyToken = async () => {
        try {
            const response = await api.get('/auth/verify');
            if (response.data.success) {
                setUser(response.data.user);
                setIsAuthenticated(true);
            } else {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
            }
        } catch (error) {
            console.error('Token verification failed:', error);
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        } finally {
            setLoading(false);
        }
    };

    // Login function - Step 1: Verify credentials, triggers OTP
    const login = async (email, password) => {
        try {
            const response = await api.post('/auth/login', { email, password });
            
            if (response.data.success) {
                // Check if 2FA is required
                if (response.data.requires2FA) {
                    return { 
                        success: true, 
                        requires2FA: true,
                        message: response.data.message 
                    };
                }
                
                // No 2FA - complete login (fallback)
                const { token, user } = response.data;
                localStorage.setItem('token', token);
                localStorage.setItem('user', JSON.stringify(user));
                setUser(user);
                setIsAuthenticated(true);
                return { success: true };
            }
            
            return { success: false, message: response.data.message };
            
        } catch (error) {
            console.error('Login error:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Login failed. Please try again.' 
            };
        }
    };

    // Verify OTP - Step 2: Complete login with 2FA code
    const verifyOTP = async (email, otp) => {
        try {
            const response = await api.post('/auth/verify-otp', { email, otp });
            
            if (response.data.success) {
                const { token, user } = response.data;
                localStorage.setItem('token', token);
                localStorage.setItem('user', JSON.stringify(user));
                setUser(user);
                setIsAuthenticated(true);
                return { success: true };
            }
            
            return { success: false, message: response.data.message };
            
        } catch (error) {
            console.error('OTP verification error:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Verification failed. Please try again.' 
            };
        }
    };

    // Resend OTP
    const resendOTP = async (email) => {
        try {
            const response = await api.post('/auth/resend-otp', { email });
            return { 
                success: response.data.success, 
                message: response.data.message 
            };
        } catch (error) {
            console.error('Resend OTP error:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Failed to resend code.' 
            };
        }
    };

    // Logout function
    const logout = async () => {
        try {
            await api.post('/auth/logout');
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            setIsAuthenticated(false);
        }
    };

    const value = {
        user,
        loading,
        isAuthenticated,
        login,
        verifyOTP,
        resendOTP,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use auth context
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};
